import { useSelector } from 'react-redux'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import { toast } from '@sales-center/components'
import { updateLoadingState } from '@sales-center/common'
import {
  selectAllRoles,
  selectRolesFilters,
  setPage,
  setSelectedRole,
} from '@user/store/roles/slice'
import { deleteRole, getRoles } from '@user/store/roles/api'
import { Role } from '@user/store/roles/typings'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { GridDensity, GridState } from '@mui/x-data-grid-pro'

export const useData = () => {
  const roles = useSelector(selectAllRoles)
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const dispatch = useAppDispatch()
  const {
    orderBy,
    page,
    perPage: rowsPerPage,
    selectedRole,
  } = useSelector(selectRolesFilters)

  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'fail'>(
    'idle',
  )
  const [searchText, setSearchText] = useState<string>('')
  const [searchResultCount, setSearchResultCount] = useState<number>(0)
  const [sideSheetOpen, setSideSheetOpen] = useState<boolean>(false)
  const [tableRows, setTableRows] = useState<Role[]>(roles)

  useEffect(() => {
    let mounted = true

    const execute = async () => {
      try {
        setStatus('pending')
        dispatch(updateLoadingState('visible'))
        const result = await dispatch(getRoles(false))

        if (mounted) {
          if (getRoles.fulfilled.match(result)) {
            setStatus('success')
          } else {
            setStatus('fail')
            toast.error(`Error loading roles list`)
          }
        }
      } catch (error) {
        if (mounted) {
          setStatus('fail')
        }
      } finally {
        dispatch(updateLoadingState('hidden'))
      }
    }
    execute()

    return () => {
      mounted = false
    }
  }, [dispatch])

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSearchText(e.target.value)
    dispatch(setPage(1))
  }

  const onDeleteRole = useCallback(
    async (roleId: number) => {
      try {
        dispatch(updateLoadingState('visible'))
        const result = await dispatch(deleteRole(roleId))
        if (deleteRole.fulfilled.match(result)) {
          toast.success(`Role deleted successfully`)
        } else {
          toast.error('Error deleting role')
        }
      } finally {
        dispatch(updateLoadingState('hidden'))
      }
    },
    [dispatch],
  )

  const handleSideSheetClose = useCallback(() => {
    setSideSheetOpen(false)
    dispatch(setSelectedRole(undefined))
  }, [dispatch])

  const handleCreateEditRole = useCallback(
    (role?: Role) => {
      setSideSheetOpen(true)
      dispatch(setSelectedRole(role))
    },
    [dispatch],
  )

  const densityModeUserGeneralPreference = userGeneralPreferences?.find(
    (row) => row?.type == IUserPreferenceGeneralListItemType.RolesDensityMode,
  )

  const pageSizeRoleGeneralPreference = userGeneralPreferences?.find(
    (row) => row?.type == IUserPreferenceGeneralListItemType.RolesPageSize,
  )

  const perPage = Number(pageSizeRoleGeneralPreference?.value || rowsPerPage)
  const stringValue = densityModeUserGeneralPreference?.value ?? 'standard'
  const densityMode = stringValue as GridDensity

  const onDensityChange = (density: string) => {
    if (densityMode != density) {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.RolesDensityMode,
          value: density,
        }),
      )
    }
  }

  const onStateChange = (state: GridState) => {
    if (state.density) {
      onDensityChange(state.density.value)
    }
  }

  const processedRows = useMemo(() => {
    const filteredRows = roles.filter((row) =>
      row.name.toLowerCase().includes(searchText.toLowerCase()),
    )

    const [field, direction] = orderBy.split(' ')
    const order = direction === 'desc' ? -1 : 1
    const fieldName = field.toLowerCase() === 'rolename' ? 'name' : field

    const sortedRows = [...filteredRows].sort((a, b) => {
      if (
        typeof a[fieldName] === 'string' &&
        typeof b[fieldName] === 'string'
      ) {
        return a[fieldName].localeCompare(b[fieldName]) * order
      }
      if (
        typeof a[fieldName] === 'number' &&
        typeof b[fieldName] === 'number'
      ) {
        return (a[fieldName] - b[fieldName]) * order
      }
      return 0
    })

    const startIndex = (page - 1) * perPage
    const paginatedRows = sortedRows.slice(startIndex, startIndex + perPage)

    return {
      paginatedRows,
      total: filteredRows.length,
    }
  }, [roles, searchText, orderBy, page, perPage])

  useEffect(() => {
    setTableRows(processedRows.paginatedRows)
    setSearchResultCount(processedRows.total)
  }, [processedRows])

  return {
    roleCount: roles.length,
    rolesLoading: status === 'pending',
    searchResultCount,
    orderBy,
    page,
    perPage,
    onDeleteRole,
    sideSheetOpen,
    handleSideSheetClose,
    handleCreateEditRole,
    densityMode,
    onStateChange,
    selectedRole,
    handleSearchChange,
    searchText,
    tableRows,
  }
}
